import React, { useContext, useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { DealerSearchContext } from '@repo/contexts/DealerSearchContext';
import OutsideClickHandler from 'react-outside-click-handler';
import FocusTrap from 'focus-trap-react';
import {
  BsChevronDown,
  BsChevronUp,
  BsChevronRight,
  BsGeoAlt,
} from 'react-icons/bs';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

// eslint-disable-next-line import/no-cycle
import SearchResults from './SearchResults';
import sharedStyles from '../styles';
import breakpoints from '../breakpoints';
import parsePhoneNumber from './parsePhoneNumber';

const TriggerWrapper = styled.div`
  position: relative;
  display: flex;
  background: ${({ active, theme }) =>
    active ? theme.button.primary.backgroundHover : 'transparent'};
  justify-content: center;
  box-sizing: border-box;
  padding: calc(${sharedStyles.spacing['spacing-sm']}) 0;
  > button {
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.brandSecondary};
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    cursor: pointer;
    span {
      margin-left: 0.5rem;
      margin-right: 0.5rem;
      padding-top: 0.125rem;
      font-size: 0.8125rem;
      &:hover {
        text-decoration: underline;
      }
    }
  }
  @media (min-width: ${breakpoints.lg}) {
    padding: ${sharedStyles.spacing['spacing-1xs']} 1rem;
    height: 2rem;
    flex: 1;
    width: 20rem;
    button {
      flex: 1;
    }
  }
`;

const DealerNumber = styled.span`
  font-weight: bold;
  margin-left: 2rem;
  font-size: ${sharedStyles.fontSize.sm};
  display: flex;
  align-items: center;
  a {
    color: ${({ theme }) => theme.colors.brandSecondary};
  }
`;

const StoredDealerPanel = styled.div`
  background: white;
  position: absolute;
  top: 2.5rem;
  box-shadow: 0px 1px 15px 5px rgba(0, 0, 0, 0.25);
  padding: 0 1rem;
  left: 0;
  right: 0;
  box-sizing: border-box;
  button {
    cursor: pointer;
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.brandPrimary};
    display: flex;
    margin-top: -1rem;
    margin-bottom: 1rem;
    width: 100%;
    justify-content: flex-end;
    align-items: center;
    font-size: ${sharedStyles.fontSize.base};
    span {
      margin-right: ${sharedStyles.spacing['spacing-1xs']};
    }
  }
  @media (min-width: ${breakpoints.sm}) {
    width: 20rem;
    left: calc(50vw - 11rem);
    right: auto;
  }
  @media (min-width: ${breakpoints.lg}) {
    width: auto;
    left: 0;
    top: 2rem;
  }
`;

function DealerSearchTrigger() {
  const { tt } = useUiComponentsInternationalContext();

  const {
    toggleDealerSearch,
    storedDealer,
    setFirstLoad,
    setStoredDealerPanelVisible,
  } = useContext(DealerSearchContext);
  const [storedDealerVisible, setStoredDealerVisible] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setStoredDealerPanelVisible(storedDealerVisible);
  }, [storedDealerVisible]);

  return (
    <OutsideClickHandler onOutsideClick={() => setStoredDealerVisible(false)}>
      <TriggerWrapper active={storedDealerVisible}>
        {storedDealer && storedDealerVisible && (
          <FocusTrap
            focusTrapOptions={{
              clickOutsideDeactivates: true,
            }}
          >
            <StoredDealerPanel>
              <SearchResults
                dealerSearchResults={[storedDealer]}
                hideSelectedDealerNotice
              />
              <button
                type="button"
                onClick={() => {
                  // clear out previous search results
                  setFirstLoad(true);
                  toggleDealerSearch();
                }}
              >
                <span>{tt(s => s.dealerCommon.searchAllDealersButton)}</span>
                <BsChevronRight
                  size={16}
                  fill={theme.colors.brandPrimary}
                  aria-hidden="true"
                />
              </button>
            </StoredDealerPanel>
          </FocusTrap>
        )}
        {!storedDealer && (
          <button
            type="button"
            onClick={() => {
              toggleDealerSearch();
            }}
          >
            <BsGeoAlt
              size={16}
              fill={theme.colors.brandSecondary}
              aria-hidden="true"
            />
            <span>{tt(s => s.dealerCommon.findLocalDealerButton)}</span>
          </button>
        )}

        {storedDealer && (
          <>
            <button
              type="button"
              onClick={() => setStoredDealerVisible(!storedDealerVisible)}
            >
              <BsGeoAlt
                size={16}
                fill={theme.colors.brandSecondary}
                aria-hidden="true"
              />
              <span>{tt(s => s.dealerCommon.yourLocalDealerButton)}</span>
              {storedDealerVisible ? (
                <BsChevronUp
                  size={16}
                  fill={theme.colors.brandSecondary}
                  aria-hidden="true"
                />
              ) : (
                <BsChevronDown
                  size={16}
                  fill={theme.colors.brandSecondary}
                  aria-hidden="true"
                />
              )}
            </button>
            <DealerNumber>
              <a href={parsePhoneNumber(`+1${storedDealer.phone}`).getURI()}>
                <strong>
                  {parsePhoneNumber(`${storedDealer.phone}`).formatNational()}
                </strong>
              </a>
            </DealerNumber>
          </>
        )}
      </TriggerWrapper>
    </OutsideClickHandler>
  );
}

export default DealerSearchTrigger;
